package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.dom.span
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/**
 * Top bar layout:
 *
 *  ┌──────────┬─────────────── count ───────────────┬──────────────┐
 *  │ Logo     │                                      │ search ⚲ ★ ⚙ │
 *  └──────────┴──────────────────────────────────────┴──────────────┘
 *
 * Left:   FlightView logotype.
 * Center: aircraft count + "updated HH:MM:SS" -- absolutely positioned
 *         at the topbar's midpoint so it stays visually centered
 *         regardless of the left/right groups' widths. `pointer-events:
 *         none` in CSS so clicks pass through.
 * Right:  search input with clear (×) button, plus filter / bookmarks /
 *         settings icons. The first two highlight when the associated
 *         state is "active".
 */
fun mountTopBar(parent: Element, app: WebAppContainer) {
    parent.div(className = "fv-topbar") {
        val leftGroup = div(className = "fv-topbar__left")
        // FlightView logotype (resources/logo.svg). Rendered as an <img> so
        // the browser caches it once and it stays crisp at any DPI without
        // the font-loading flash you'd get from rendering "FlightView" as
        // plain text. Alt text keeps it accessible.
        val logo = document.createElement("img") as HTMLImageElement
        // Absolute path so the request resolves to /logo.svg regardless of
        // the current URL (e.g. on /flight/<hex> or /airport/<code> deep
        // links, a relative "logo.svg" would hit /flight/logo.svg and 404).
        logo.src = "/logo.svg"
        logo.alt = "FlightView"
        logo.className = "fv-topbar__logo"
        leftGroup.appendChild(logo)

        // Absolutely-centered count. Sibling of the left/right groups rather
        // than a child, so its horizontal position is anchored to the topbar
        // itself (not to whatever widths the left/right groups happen to be).
        val countSpan = span(className = "fv-topbar__count")

        app.appState.scope.bindFlow(app.appState.aircraftCount) { count ->
            renderCountText(countSpan, count, app.appState.lastUpdateTime.value)
        }
        app.appState.scope.bindFlow(app.appState.lastUpdateTime) { time ->
            renderCountText(countSpan, app.appState.aircraftCount.value, time)
        }

        // Search input + inline clear (×). Live filter -- no debounce needed,
        // filtering is in-memory and the list is small (hundreds, not thousands).
        val searchWrap = div(className = "fv-topbar__search-wrap")
        val searchBox = searchWrap.div(className = "fv-topbar__search-box")

        val searchInput = document.createElement("input") as HTMLInputElement
        searchInput.type = "text"
        searchInput.className = "fv-topbar__search"
        searchInput.placeholder = "Search: DAL123, JFK, B738\u2026"
        searchInput.autocomplete = "off"
        searchInput.value = app.uiState.searchQuery.value
        searchBox.appendChild(searchInput)

        // Clear button. Sits absolutely positioned on the right edge of the
        // input; shown only when the input has content (via a has-content
        // class on the parent). Click clears both the input and the shared
        // search flow.
        val clearBtn = document.createElement("button") as org.w3c.dom.HTMLButtonElement
        clearBtn.className = "fv-topbar__search-clear"
        clearBtn.type = "button"
        clearBtn.textContent = "\u2715"
        clearBtn.title = "Clear search"
        clearBtn.setAttribute("aria-label", "Clear search")
        clearBtn.addEventListener("click", { _: Event ->
            searchInput.value = ""
            app.uiState.setSearchQuery("")
            searchBox.classList.remove("has-content")
            searchInput.focus()
        })
        searchBox.appendChild(clearBtn)

        // Two-way wiring for the input:
        // 1. Typing updates the shared search flow.
        // 2. The has-content class toggles the × visibility.
        // 3. If the flow changes elsewhere (e.g. via the clear button, or a
        //    future shortcut), reflect it back into the DOM.
        searchInput.addEventListener("input", { _: Event ->
            app.uiState.setSearchQuery(searchInput.value)
        })

        // Enter = "jump to airport" if the query matches a known IATA or
        // US ICAO code (e.g. "JFK", "KJFK", "LAX"). Selecting the airport
        // opens the AirportPanel and the selectedAirport flow pans the
        // map; we also fire requestMapFocus so a re-selection of the
        // same code still recenters (same-value StateFlow sets are
        // no-ops). Unrecognised codes get a transient status message so
        // the user knows Enter did something. Non-airport queries
        // (callsigns, types) fall through untouched -- the live filter
        // still applies them to the aircraft list.
        searchInput.addEventListener("keydown", { ev: Event ->
            val ke = ev as? KeyboardEvent ?: return@addEventListener
            if (ke.key != "Enter") return@addEventListener
            val raw = searchInput.value.trim()
            if (raw.isEmpty()) return@addEventListener
            val apt = AirportDatabase.findLocation(raw)
            if (apt != null) {
                ke.preventDefault()
                app.appState.selectAirport(apt.code)
                app.uiState.requestMapFocus(apt.lat, apt.lon)
                searchInput.value = ""
                app.uiState.setSearchQuery("")
                searchBox.classList.remove("has-content")
                searchInput.blur()
            } else if (raw.length in 3..4 && raw.all { it.isLetterOrDigit() }) {
                // Looks airport-shaped but isn't in the database -- tell
                // the user so they don't think the key was eaten.
                val msg = "Airport ${raw.uppercase()} not recognised."
                app.appState.setStatusMessage(msg)
                window.setTimeout({
                    if (app.appState.statusMessage.value == msg) {
                        app.appState.setStatusMessage("")
                    }
                }, 5_000)
            }
        })
        app.appState.scope.bindFlow(app.uiState.searchQuery) { q ->
            if (searchInput.value != q) searchInput.value = q
            if (q.isNotEmpty()) searchBox.classList.add("has-content")
            else searchBox.classList.remove("has-content")
        }

        val rightGroup = div(className = "fv-topbar__right")

        // Filter toggle (≡ triple bar icon). Highlights blue when a filter
        // is active OR when the panel itself is open -- either signal is
        // useful ("filters on" vs. "panel is open below").
        val filterBtn = rightGroup.button(className = "fv-topbar__iconbtn") {
            textContent = "\u2261"
            title = "Filters"
        }
        filterBtn.onClick {
            app.uiState.toggleFiltersVisible()
            // Same mobile treatment as the bookmarks star: the filter
            // panel lives inside the sidebar, invisible when the Map tab
            // is active on phones. Flip to the List tab so it's
            // reachable. Only triggers when opening — closing doesn't
            // yank the user back to the map.
            if (app.uiState.filtersVisible.value) {
                switchToMobileTab("list")
            }
        }
        app.appState.scope.bindFlow(app.uiState.filtersVisible) { visible ->
            val active = app.uiState.filters.value.isActive
            filterBtn.className = if (visible || active) "fv-topbar__iconbtn fv-topbar__iconbtn--active"
            else "fv-topbar__iconbtn"
        }
        app.appState.scope.bindFlow(app.uiState.filters) { _ ->
            val visible = app.uiState.filtersVisible.value
            val active = app.uiState.filters.value.isActive
            filterBtn.className = if (visible || active) "fv-topbar__iconbtn fv-topbar__iconbtn--active"
            else "fv-topbar__iconbtn"
        }

        // Bookmarks toggle. Amber when any bookmarks exist.
        val bookmarksBtn = rightGroup.button(className = "fv-topbar__iconbtn") {
            textContent = "\u2605"
            title = "Bookmarks"
        }
        bookmarksBtn.onClick {
            app.uiState.toggleBookmarksVisible()
            // On mobile, the bookmarks panel lives inside the sidebar which
            // is hidden when the Map tab is active. Flip the user to the
            // List tab so the panel is actually visible. Harmless on
            // desktop (the attribute controls a mobile-only layout).
            // We only auto-switch when we're TURNING ON bookmarks — turning
            // off shouldn't yank the user back to the map.
            if (app.uiState.bookmarksVisible.value) {
                switchToMobileTab("list")
            }
        }
        app.appState.scope.bindFlow(app.appState.bookmarks) { list ->
            bookmarksBtn.className = if (list.isNotEmpty()) "fv-topbar__iconbtn fv-topbar__iconbtn--amber"
            else "fv-topbar__iconbtn"
        }

        // Settings button. Keep label explicit for discoverability on mobile;
        // CSS shrinks it to a gear icon at narrow widths.
        val settingsBtn = rightGroup.button(className = "fv-topbar__iconbtn fv-topbar__settings") {
            textContent = "\u2699"
            title = "Settings"
        }
        settingsBtn.onClick {
            app.alertManager.ensurePermission()
            showSettingsDialog(app) { /* nothing */ }
        }
    }
}

private fun renderCountText(target: Element, count: Int, lastUpdate: String?) {
    target.textContent = buildString {
        append(count)
        append(" aircraft")
        if (!lastUpdate.isNullOrBlank()) {
            append(" \u00B7 updated ")
            append(lastUpdate)
        }
    }
}

/**
 * Programmatically switch the mobile view tab by clicking the matching
 * bottom-nav button. Delegating to the click handler instead of setting
 * the attribute directly keeps the nav-button active-state in sync and
 * fires the Leaflet-resize event the map needs when it un-hides.
 *
 * No-op on desktop: the bottom nav is CSS-hidden and the attribute is
 * ignored by the desktop layout.
 */
private fun switchToMobileTab(tab: String) {
    val btn = document.querySelector(".fv-bottomnav__btn[data-tab=\"$tab\"]")
        as? org.w3c.dom.HTMLButtonElement
        ?: return
    btn.click()
}
